import {Button, Col, Form, InputGroup, Modal, Row, Spinner} from 'react-bootstrap'
import {IAllUserNode, IResCreateMember, IResEditMember} from '../../../types/users'
import {useEffect, useRef, useState} from 'react'
import {useMutation, useQuery} from '@apollo/client'
import {toast} from 'react-toastify'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {CREATE_USER, EDIT_USER} from '../../../gql/mutations/userMutations'
import {parseId} from '../../../helpers'
import {GET_ROLES_GROUP} from '../../../gql/queries/rolesGroupQuery'
import {IResRolesGroups} from '../../../types/roles'
import {useGetUser} from '../../../store/userStore'
import {GET_ALL_ORGANIZATIONS} from '../../../gql/queries/organizationsQuery'
import {REQUEST_PASSWORD_RESET} from '../../../gql/mutations/authMutations'

const INIT_VALUES = {
  firstName: '',
  lastName: '',
  email: '',
  cellphone: '',
  password: '',
  confirmPassword: '',
  rol: '',
  organizationId: '',
  sendInvitation: true,
}

const validationSchema = Yup.object({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  email: Yup.string().email('Invalid email format').required('Email is required'),
  cellphone: Yup.string(),
  rol: Yup.string().required('Role is required'),
  organizationId: Yup.string(),
  sendInvitation: Yup.boolean(),
  password: Yup.string().when('sendInvitation', {
    is: false,
    then: (schema) => schema.min(6, 'Password must be at least 6 characters').required('Password is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  confirmPassword: Yup.string().when('sendInvitation', {
    is: false,
    then: (schema) =>
      schema.oneOf([Yup.ref('password')], 'Passwords must match').required('Confirm password is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
})

interface IModalAddUserProps {
  isOpen: true
  onClose: () => void
  refetch: () => void
  user?: IAllUserNode
}

export const ModalAddUser: React.FC<IModalAddUserProps> = ({isOpen, onClose, refetch, user}) => {
  // Get current user's role
  const userGroups = useGetUser((s) => s.user?.groups) || []
  const currentUser = useGetUser((s) => s.user?.user)
  const currentUserId = currentUser?.id
  const currentUserOrganizationId = currentUser?.organization?.id

  // Check user roles
  const isAgente = userGroups.includes('Agente')
  const isAsesor = userGroups.includes('Asesor')
  const isSupervisor = userGroups.includes('Supervisor')
  const isAdministrador = userGroups.includes('Administrador')
  const isSuperAdministrador = userGroups.includes('SuperAdministrador')
  const isEditingSelf = !!(user && currentUserId && user.id === currentUserId)

  // Determine if role selector should be disabled
  const isRoleSelectorDisabled = !!(isAgente || isAsesor) && isEditingSelf

  const {
    data: dataRoles,
    loading: loadingRoles,
    error: errorRoles,
  } = useQuery<IResRolesGroups>(GET_ROLES_GROUP, {fetchPolicy: 'no-cache'})

  const {
    data: dataOrganizations,
    loading: loadingOrganizations,
    error: errorOrganizations,
  } = useQuery(GET_ALL_ORGANIZATIONS, {fetchPolicy: 'no-cache'})
  
  const [gqlCreate, {data, loading, error}] = useMutation<IResCreateMember>(CREATE_USER)
  const [gqlEdit, {data: dataEdit, loading: loadingEdit, error: errorEdit}] =
    useMutation<IResEditMember>(EDIT_USER)
  const [requestInvite] = useMutation(REQUEST_PASSWORD_RESET)

  const formik = useFormik({
    initialValues: INIT_VALUES,
    validationSchema,

    onSubmit: (v) => {
      const {sendInvitation, ...values} = v
      const applyDeductionsValue = user ? user.applyDeductions : true

      // Si es Administrador o Supervisor, asignar su propia organización automáticamente
      let organizationIdToSend = values.organizationId ? parseId(values.organizationId) : undefined
      if ((isAdministrador || isSupervisor) && !user && currentUserOrganizationId) {
        organizationIdToSend = parseId(currentUserOrganizationId)
      }

      const userData = {
        ...values,
        applyDeductions: applyDeductionsValue,
        confirmPassword: undefined,
        primaryGroup: values.rol ? parseId(values.rol) : undefined,
        organizationId: organizationIdToSend,
        isActive: true,
        rol: undefined,
        password: sendInvitation ? undefined : values.password,
      }
      if (user) {
        gqlEdit({
          variables: {
            userData,
            userId: parseId(user.id),
          },
        })
      } else {
        inviteRequestRef.current = {
          email: values.email,
          enabled: sendInvitation,
        }
        gqlCreate({
          variables: {
            userData,
          },
        })
      }
    },
  })

  const [showPass, setShowPass] = useState(false)
  const [showConfirmPass, setShowConfirmPass] = useState(false)
  const inviteRequestRef = useRef<{email: string; enabled: boolean} | null>(null)

  useEffect(() => {
    if (!user) return
    formik.setValues({
      cellphone: user.cellphone || '',
      confirmPassword: '',
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      password: '',
      rol: user.primaryGroup ? user.primaryGroup.id : '',
      organizationId: user.organization ? user.organization.id : '',
      sendInvitation: true,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  useEffect(() => {
    if (error) toast.error(`Error: ${error.message}`)
  }, [error])
  useEffect(() => {
    if (errorEdit) toast.error(`Error: ${errorEdit.message}`)
  }, [errorEdit])
  useEffect(() => {
    if (errorRoles) toast.error(`Error: ${errorRoles.message}`)
  }, [errorRoles])
  useEffect(() => {
    if (errorOrganizations) toast.error(`Error: ${errorOrganizations.message}`)
  }, [errorOrganizations])

  useEffect(() => {
    if (!data) return
    if (data?.createUserMutation?.user?.id) {
      const inviteRequest = inviteRequestRef.current
      const runInvite = async () => {
        toast.success('user added successfully')
        if (inviteRequest?.enabled) {
          try {
            const response = await requestInvite({
              variables: {email: inviteRequest.email, isInvitation: true},
            })
            const message =
              response.data?.requestPasswordReset?.message || 'Invitation sent.'
            toast.success(message)
          } catch {
            toast.error('Unable to send invitation email.')
          }
        }
        inviteRequestRef.current = null
        refetch()
        onClose()
      }
      runInvite()
    } else if (data?.createUserMutation.errors?.length > 0) {
      toast.error(`Error: ${data?.createUserMutation.errors[0]?.message || 'Something wrong!'}`)
      inviteRequestRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])
  useEffect(() => {
    if (!dataEdit) return
    if (dataEdit?.updateUserMutation?.user?.id) {
      toast.success('user updated successfully')
      refetch()
      onClose()
    } else if (dataEdit?.updateUserMutation.errors?.length > 0) {
      toast.error(`Error: ${dataEdit?.updateUserMutation.errors[0]?.message || 'Something wrong!'}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataEdit])

  return (
    <Modal show={isOpen} onHide={onClose} size='lg'>
      <Modal.Header closeButton>
        <Modal.Title>Invite a new user</Modal.Title>
      </Modal.Header>
      <form onSubmit={formik.handleSubmit}>
        <Modal.Body>
          <Row className='g-2'>
            <Col md={6} sm={12}>
              <Form.Group>
                <Form.Label>
                  First name <small className='text-danger'>(required)</small>
                </Form.Label>
                <Form.Control
                  placeholder='First name'
                  name='firstName'
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  isInvalid={Boolean(formik.errors.firstName && formik.touched.firstName)}
                />
                <Form.Control.Feedback type='invalid'>
                  {formik.errors.firstName}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6} sm={12}>
              <Form.Group>
                <Form.Label>
                  Last name <small className='text-danger'>(required)</small>
                </Form.Label>
                <Form.Control
                  placeholder='Last name'
                  name='lastName'
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  isInvalid={Boolean(formik.errors.lastName && formik.touched.lastName)}
                />
                <Form.Control.Feedback type='invalid'>
                  {formik.errors.lastName}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6} sm={12}>
              <Form.Group>
                <Form.Label>
                  Email address <small className='text-danger'>(required)</small>
                </Form.Label>
                <Form.Control
                  placeholder='Email address'
                  type='email'
                  name='email'
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  isInvalid={Boolean(formik.errors.email && formik.touched.email)}
                />
                <Form.Control.Feedback type='invalid'>{formik.errors.email}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6} sm={12}>
              <Form.Group>
                <Form.Label>Cellphone number</Form.Label>
                <Form.Control
                  placeholder='Cellphone'
                  type='tel'
                  name='cellphone'
                  value={formik.values.cellphone}
                  onChange={(e) => {
                    let v = e.target.value
                    // Permite "+" solo al inicio y dígitos; elimina otros caracteres
                    v = v.replace(/(?!^\+)[^\d]/g, '')        // quita todo lo que no sea dígito excepto un '+' inicial
                    v = v.replace(/(?!^)\+/g, '')             // quita '+' extra si los hay
                    formik.setFieldValue('cellphone', v)
                  }}
                  maxLength={15}
                  isInvalid={Boolean(formik.errors.cellphone && formik.touched.cellphone)}
                />
                <Form.Control.Feedback type='invalid'>
                  {formik.errors.cellphone}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6} sm={12}>
              <Form.Group>
                <Form.Label>Password</Form.Label>
                <InputGroup className='mb-3'>
                  <Form.Control
                    type={showPass ? 'text' : 'password'}
                    placeholder='Insert password'
                    aria-label='Insert password'
                    aria-describedby='password'
                    name='password'
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    disabled={formik.values.sendInvitation}
                    isInvalid={Boolean(formik.errors.password && formik.touched.password)}
                  />
                  <Button
                    variant='outline-secondary'
                    id='button-addon2'
                    style={{border: '1px solid #dbdfe9'}}
                    onClick={() => setShowPass((p) => !p)}
                  >
                    <i
                      className={`bi bi-${showPass ? 'eye-slash' : 'eye'} fs-2`}
                      style={{color: 'inherit'}}
                    />
                  </Button>
                </InputGroup>
                {formik.errors.password && formik.touched.password && (
                  <small className='text-danger'>{formik.errors.password}</small>
                )}
              </Form.Group>
            </Col>
            <Col md={6} sm={12}>
              <Form.Group>
                <Form.Label>Confirm Password</Form.Label>
                <InputGroup className='mb-3'>
                  <Form.Control
                    type={showConfirmPass ? 'text' : 'password'}
                    placeholder='Confirm password'
                    aria-label='Confirm password'
                    aria-describedby='Confirm password'
                    name='confirmPassword'
                    value={formik.values.confirmPassword}
                    onChange={formik.handleChange}
                    disabled={formik.values.sendInvitation}
                    isInvalid={Boolean(
                      formik.errors.confirmPassword && formik.touched.confirmPassword
                    )}
                  />
                  <Button
                    variant='outline-secondary'
                    id='Confirm-pass'
                    onClick={() => setShowConfirmPass((p) => !p)}
                    style={{border: '1px solid #dbdfe9'}}
                  >
                    <i
                      className={`bi bi-${showConfirmPass ? 'eye-slash' : 'eye'} fs-2`}
                      style={{color: 'inherit'}}
                    />
                  </Button>
                </InputGroup>
                {formik.errors.confirmPassword && formik.touched.confirmPassword && (
                  <small className='text-danger'>{formik.errors.confirmPassword}</small>
                )}
              </Form.Group>
            </Col>

            <Col md={6} sm={12}>
              <Form.Group>
                <Form.Label>
                  Rol <small className='text-danger'>(required)</small>
                </Form.Label>
                <Form.Select
                  name='rol'
                  value={formik.values.rol}
                  onChange={formik.handleChange}
                  isInvalid={Boolean(formik.errors.rol && formik.touched.rol)}
                  disabled={loadingRoles || isRoleSelectorDisabled}
                >
                  {loading ? (
                    <option value=''>Loading...</option>
                  ) : (
                    <option value=''>Select One</option>
                  )}
                  {dataRoles?.allGroups.edges
                    .filter(({node}) => {
                      // Si no es SuperAdministrador, no puede asignar rol SuperAdministrador
                      if (!isSuperAdministrador && node.name === 'SuperAdministrador') {
                        return false
                      }
                      // Si es Supervisor, no puede crear o asignar rol de Administrador
                      if (isSupervisor && node.name === 'Administrador') {
                        return false
                      }
                      return true
                    })
                    .map(({node}) => (
                      <option key={node.id} value={node.id}>
                        {node.name}
                      </option>
                    ))}
                </Form.Select>
                <Form.Control.Feedback type='invalid'>{formik.errors.rol}</Form.Control.Feedback>
                {isRoleSelectorDisabled && (
                  <Form.Text className='text-muted'>
                    You don't have permissions to change your own role
                  </Form.Text>
                )}
              </Form.Group>
              {/* <small>
                <span className='text-primary' style={{textDecoration: 'underline'}}>
                  Click here
                </span>{' '}
                to learn more about our access roles.
              </small> */}
            </Col>
            <Col md={6} sm={12}>
              <Form.Group>
                <Form.Label>Organization</Form.Label>
                <Form.Select
                  name='organizationId'
                  value={formik.values.organizationId}
                  onChange={formik.handleChange}
                  isInvalid={Boolean(
                    formik.errors.organizationId && formik.touched.organizationId
                  )}
                  disabled={
                    loadingOrganizations
                    // TEMPORARY: Commented out SuperAdministrador restriction
                    // ((isAdministrador || isSupervisor) && !user) ||
                    // (user && !isSuperAdministrador)
                  }
                >
                  {loadingOrganizations ? (
                    <option value=''>Loading...</option>
                  ) : (
                    <option value=''>Select One</option>
                  )}
                  {dataOrganizations?.allOrganizations?.edges?.map(({node}: any) => (
                    <option key={node.id} value={node.id}>
                      {node.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type='invalid'>
                  {formik.errors.organizationId}
                </Form.Control.Feedback>
                {((isAdministrador || isSupervisor) && !user) && (
                  <Form.Text className='text-muted'>
                    New users will automatically be assigned to your organization
                  </Form.Text>
                )}
                {user && !isSuperAdministrador && (
                  <Form.Text className='text-muted'>
                    Only SuperAdministrador can change the organization
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            {/* <Col md={6} sm={12}>
              <Form.Group>
                <Form.Label>
                  Preferred language <small className='text-danger'>(required)</small>
                </Form.Label>
                <Form.Select>
                  <option>Spanish</option>
                  <option>English</option>
                </Form.Select>
              </Form.Group>
            </Col> */}
            {!user && (
              <Col className='mt-5'>
                <Form.Check
                  type='checkbox'
                  name='sendInvitation'
                  checked={formik.values.sendInvitation}
                  onChange={(e) => {
                    const checked = e.target.checked
                    formik.setFieldValue('sendInvitation', checked)
                    if (checked) {
                      formik.setFieldValue('password', '')
                      formik.setFieldValue('confirmPassword', '')
                    }
                  }}
                  label={
                    <span className='text-dark'>
                      Send invitation email to set credentials.
                    </span>
                  }
                />
              </Col>
            )}
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button type='button' variant='secondary' onClick={onClose}>
            Close
          </Button>
          <Button type='submit' variant='primary' disabled={loading || loadingEdit}>
            {(loading || loadingEdit) && <Spinner animation='border' size='sm' />}
            Save Changes
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}
